<template>
  <v-btn
    class="mbtn font-weight-regular"
    prepend-icon="ct:arrow-left"
    @click="personalDataStep -= 1"
  >
    {{ t("buyout.appraisal.go_back") }}
  </v-btn>
  <div class="app__title app__title--h3">
    {{ t('buyout.appraisal.delivery_and_payout') }}
  </div>
  <div class="buyout__summary">
    <div class="wrapper d-grid">
      <div class="app__title app__title--h4">
        {{ t('buyout.appraisal.choose_delivery_old_device') }}
      </div>
      <buyout-buyout-detail-delivery-selector-window
        ref="deliverySelectorWindow"
        :order-delivery-later-available="true"
        @scroll-to-bank-info="scrollToBankInfo"
      />
      <template v-if="!selectedDeliveryType || !(isNewDeviceProcess && selectedDeliveryType.identifier==='cashtec_brand_store_handover')">
        <div class="app__title app__title--h4">
          {{ t("buyout.appraisal.bank_info_for_money") }}
        </div>
        <v-alert class="vAlert--success vAlert--noBorder vAlert--compact" icon="ct:money">
          <template #title>
            {{ t('buyout.appraisal.money_up_to_48_hours') }}
          </template>
        </v-alert>
      </template>
      <v-form id="bankInfoForm" ref="form" class="app__form">
        <template v-if="!selectedDeliveryType || !(isNewDeviceProcess && selectedDeliveryType.identifier==='cashtec_brand_store_handover')">
          <template v-if="isIBAN">
            <div class="formItem formItem--1x12">
              <v-text-field
                v-model="buyoutPersonalData.iban"
                :placeholder="t('form.placeholders.iban')"
                :label="t('form.common.iban')"
                required
                :error-messages="get(buyoutPersonalDataErrors, 'iban')"
                :rules="[rules.required, rules.iban]"
                name="customer_data_iban"
                @change="buyoutStore.resetPersonalDataErrors('iban')"
              />
            </div>
          </template>
          <template v-else>
            <div class="formItem formItem--1x9">
              <v-text-field
                v-model="buyoutPersonalData.bank_account_number"
                :placeholder="t('form.placeholders.bank_account_number_ph')"
                :label="t('form.common.bank_account_number')"
                required
                :error-messages="get(buyoutPersonalDataErrors, 'bank_account_number')"
                :rules="[rules.required, rules.bankAccountNumber]"
                name="customer_data_bank_account_number"
                @change="buyoutStore.resetPersonalDataErrors('bank_account_number')"
              />
            </div>
            <div class="formItem formItem--1x3">
              <v-text-field
                v-model="buyoutPersonalData.bank_code"
                :placeholder="t('form.placeholders.bank_code_ph')"
                :label="t('form.common.bank_code')"
                required
                :error-messages="get(buyoutPersonalDataErrors, 'bank_code')"
                :rules="[rules.required, rules.bankCode]"
                name="customer_data_bank_code"
                @change="buyoutStore.resetPersonalDataErrors('bank_code')"
              />
            </div>
          </template>
        </template>
        <div class="formItem formItem--1x12">
          <v-checkbox v-if="samsungPrivacyPolicyConfirmationRequired" :rules="[rules.requiredTrue]">
            <template #label>
              <i18n-t keypath="buyout.bonus.agree_privacy_policy_1" tag="span">
                <template #link>
                  <nuxt-link
                    class="color--blueTone1"
                    :to="localePath({name: 'partner-samsung-privacy-policy', params: {partner: partner}})"
                    target="_blank"
                  >
                    {{ t("buyout.bonus.agree_privacy_policy_2") }}
                  </nuxt-link>
                </template>
              </i18n-t>
            </template>
          </v-checkbox>
          <!--          <v-checkbox-->
          <!--            v-if="vodafoneCompetitionTerms"-->
          <!--            v-model="agree_vodafone_competition_terms"-->
          <!--          >-->
          <!--            <template #label>-->
          <!--              <i18n-t keypath="buyout.bonus.agree_competition_rules_1" tag="span">-->
          <!--                <template #link>-->
          <!--                  <a-->
          <!--                    class="color&#45;&#45;blueTone1"-->
          <!--                    href="https://www.vodafone.cz/vykup-telefonu/"-->
          <!--                    target="_blank"-->
          <!--                  >-->
          <!--                    {{ t("buyout.bonus.agree_competition_rules_2") }}-->
          <!--                  </a>-->
          <!--                </template>-->
          <!--              </i18n-t>-->
          <!--            </template>-->
          <!--          </v-checkbox>-->
        </div>
      </v-form>
      <bonus-manufacturer-buyout-new-send-email-window v-if="authStore.isPartner && isNewDeviceProcess && !selectedDeliveryType " />
      <v-btn
        v-else
        class="mbtn mbtn--green mt-4"
        size="x-large"
        :loading="deliverySelectorWindow?.moveToThankYouLoading"
        @click="moveToThankYou"
      >
        {{ isNewDeviceProcess && authStore.isPartner && selectedDeliveryType?.identifier==='cashtec_brand_store_handover' ? t("buyout.appraisal.complete_buyout_and_print_contract") : t("buyout.appraisal.complete_buyout") }}
      </v-btn>
    </div>
    <buyout-device-summary-tile :is-new-device-process="isNewDeviceProcess" />
  </div>
</template>

<script setup lang="ts">
import { get, some } from "lodash-es";
import { storeToRefs } from "pinia";
import { VForm } from "vuetify/components";
import type DeliverySelectorWindow from "./buyout-detail/deliverySelectorWindow.vue";

const { rules } = useFormValidators();
const buyoutStore = useBuyoutStore();
const partnerStore = usePartnerStore();
const authStore = useAuthStore();
const localePath = useLocalePath();
const { notify } = useNotification();
const { t } = useI18n();
const api = useApi();

withDefaults(defineProps<{
  isNewDeviceProcess?: boolean
}>(), {
  isNewDeviceProcess: false
});

const {
  buyoutPersonalData,
  buyoutPersonalDataErrors,
  personalDataStep,
  selectedBuyoutBonuses,
  selectedDeliveryType
} = storeToRefs(buyoutStore);

interface ShowIBAN {
  show_iban: boolean;
}

const isIBAN = ref<null | boolean>(null);

async function loadShowIBAN () {
  if (isIBAN.value === null) {
    const { data, error } = await api.call<ShowIBAN>(api.commonUrls.common.SHOW_IBAN());
    if (error.value) {
      notify({
        title: t("form.common.error"),
        text: error.value!.data || t("general.error_sending_request"),
        type: "error"
      });
    } else {
      isIBAN.value = data.value.show_iban;
    }
  }
}

// interface VodafoneCompetition {
//   agreed_to_competition_terms: boolean;
// }
//
// const agree_vodafone_competition_terms = ref<boolean>(false);

// async function saveVodafoneTerms () {
//   if (vodafoneCompetitionTerms.value && agree_vodafone_competition_terms.value) {
//     const { error } = await api.call<VodafoneCompetition>(api.commonUrls.buyout.COMPETITION_AGREEMENT(), {
//       method: "PATCH",
//       body: {
//         registration_number: userBuyoutCode.value,
//         agreed_to_competition_terms: agree_vodafone_competition_terms.value
//       }
//     });
//     if (error.value) {
//       notify({
//         title: t("form.common.error"),
//         text: error.value!.data || t("general.error_sending_request"),
//         type: "error"
//       });
//     }
//   }
// }

onMounted(loadShowIBAN);
const form = ref<VForm | null>(null);

useMoveToTopOfPage();

async function moveToThankYou () {
  // saveVodafoneTerms();
  if (!(await form!.value!.validate())!.valid) {
    await useGoTo("#bankInfoForm", { offset: -450 });
    return;
  }
  deliverySelectorWindow?.value?.moveToThankYou();
}

function scrollToBankInfo () {
  useGoTo("#bankInfoForm", { offset: -300 });
}

const deliverySelectorWindow = ref<InstanceType<typeof DeliverySelectorWindow> | null>(null);

const partner = computed<string>(() => (partnerStore.getBuyoutPartner?.seo_name ? partnerStore.getBuyoutPartner.seo_name : "cashtec"));

const samsungPrivacyPolicyConfirmationRequired = computed<boolean>(() => some(selectedBuyoutBonuses.value, "requires_samsung_pp"));

// const vodafoneCompetitionTerms = computed<boolean>(() => partnerStore.getBuyoutPartner?.seo_name==="vodafone" && ["A","B","C"].includes(selectedProductQuality.value!.quality.short_name) && !["ostatni"].includes(selectedManufacturer.value!.seo_name));
</script>
